import { useNavigate, useLocation, Link } from 'react-router';
import { useEffect, useMemo } from 'react';

import { useAuth } from '@contexts/auth';
import { useNotification } from '@contexts/notification';
import { useSidebar } from '@contexts/sidebar';
import UserService from '@utility/services/user';
import { useMetadata } from '@contexts/org-metadata';
import { CommonUtility } from '@utility/common';
import { brand } from '@utility/config';
import { useSecureLayout } from '@hooks/useSecureLayout';
import { GlobalSearch } from '@layouts/components/GlobalSearch';
import { ExportReportModal } from '@features/catalog';
import { exportSheetTypes } from '@utility/constants/types';
import { OrganizationSwitcher } from './OrganizationSwitcher';
import { useIsV2Account } from '@features/page-versions';
import { ProfileMenu } from './ProfileMenu';
import {
  cn,
  SideNav,
  type SideNavEntry,
  type SideNavItem,
} from '@skupreme/shared/ui';

import {
  filterSidebarMenu,
  shouldRedirectInSummaryView,
  getActiveKey,
  buildNavigation,
  type MenuItem,
} from './utils';

function toEntryBase(item: MenuItem) {
  return {
    label: item.text,
    icon: item.phosphorIcon,
  };
}

/* Only a group may lack a link; `filterSidebarMenu` drops the rest. */
function toItem(item: MenuItem): SideNavItem[] {
  return item.link
    ? [{ ...toEntryBase(item), id: item.link, href: `${item.link}/` }]
    : [];
}

function toEntries(menus: MenuItem[]): SideNavEntry[] {
  return menus.flatMap((item): SideNavEntry[] =>
    CommonUtility.isValidArray(item.menus)
      ? [
          {
            ...toEntryBase(item),
            id: item.key,
            items: item.menus.flatMap(toItem),
          },
        ]
      : toItem(item),
  );
}

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldShowLayout = useSecureLayout();

  const {
    update: refreshNotification,
    latestNotifications,
    resetLatestNotifications,
  } = useNotification() as {
    update: () => void;
    latestNotifications: Array<{ metadata?: { userId?: string } }>;
    resetLatestNotifications: () => void;
  };

  const { user, isAuthenticated, login: providerLogin } = useAuth();

  const { isSummaryView } = useMetadata() as { isSummaryView: boolean };

  const {
    menus,
    showSidebar,
    showExportPopup,
    closeExportPopup,
    isNavOpen,
    setIsNavOpen,
  } = useSidebar() as {
    menus: MenuItem[];
    showSidebar: boolean;
    showExportPopup: boolean | null;
    closeExportPopup: () => void;
    isNavOpen: boolean;
    setIsNavOpen: (open: boolean) => void;
  };

  // The drawer (below lg) closes once a link in it is followed.
  useEffect(() => {
    setIsNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isNavOpen) {
      return undefined;
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsNavOpen(false);
      }
    };
    // Capture: a control inside the drawer may stop the key bubbling.
    document.addEventListener('keydown', closeOnEscape, true);
    return () => document.removeEventListener('keydown', closeOnEscape, true);
  }, [isNavOpen]);

  useEffect(() => {
    const temp = (latestNotifications || []).find(
      x => x.metadata?.userId === user?._id,
    );

    const refreshPermission = async () => {
      const refresh_token = UserService.getRefreshToken();
      const params = {
        refresh_token,
      };
      const result = await UserService.refreshToken(params);
      providerLogin(result);
      resetLatestNotifications();
    };

    if (temp) {
      refreshPermission();
    }
  }, [latestNotifications]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshNotification();
    }
  }, [isAuthenticated]);

  const sidebarMenu = useMemo(
    () => filterSidebarMenu(menus, isSummaryView),
    [isSummaryView, menus],
  );

  useEffect(() => {
    if (isSummaryView && CommonUtility.isValidArray(sidebarMenu)) {
      const { pathname } = window.location || {};
      if (shouldRedirectInSummaryView(pathname, sidebarMenu)) {
        navigate('/app/?tab=analytics');
      }
    }
  }, [isSummaryView, sidebarMenu, navigate]);

  const isV2Account = useIsV2Account();
  const navigation = useMemo(
    () => buildNavigation(sidebarMenu, isV2Account),
    [sidebarMenu, isV2Account],
  );

  const entries = useMemo(() => toEntries(navigation.main), [navigation]);

  const activeKey = useMemo(
    () => getActiveKey(navigation.main, location.pathname),
    [navigation, location.pathname],
  );

  if (!shouldShowLayout || !showSidebar) {
    return null;
  }

  return (
    <>
      <SideNav
        label={brand.name}
        entries={entries}
        activeKey={activeKey}
        /*
         * The height has to be definite: the column is a flex item, so left to
         * `auto` it stretches to its own content and the list never scrolls.
         * `--sk-spacing` is what the scale is called once the prefix is applied.
         *
         * `z-[2]` is off the scale on purpose — it is the stacking this column
         * has always had, and rounding it to `z-10` would move it against
         * everything else on the page.
         *
         * Flush with the window: the column lies on the frame, and its rows'
         * own padding is the gutter.
         *
         * Below lg there is no room for the column: it is a drawer over the
         * page instead, opened from the menu button in the page header and
         * hidden (`invisible`, so out of the tab order too) while closed. The
         * column has no surface of its own, so the drawer takes the frame's.
         */
        className={cn(
          'sk:sticky sk:top-0 sk:z-[2] sk:h-screen sk:shrink-0',
          'sk:max-lg:fixed sk:max-lg:left-0 sk:max-lg:z-50 sk:max-lg:bg-shell sk:max-lg:transition-[translate,visibility] sk:max-lg:duration-200',
          !isNavOpen && 'sk:max-lg:invisible sk:max-lg:-translate-x-[110%]',
        )}
        header={
          <div className="sk:flex sk:flex-col">
            {/* `px-2` is a row's own inset, so the mark lines up with the icons
                below it. `h-12` is the page title bar's height and `mt-0.5`
                plus the panel's padding is the card's inset and border, so the
                row sits level with the title and the switcher under it starts
                where the page's own toolbar does. */}
            <div className="sk:mt-0.5 sk:flex sk:h-12 sk:items-center sk:justify-between sk:px-2">
              <Link
                to="/app/dashboard/awaiting-actions/"
                className="sk:flex sk:items-center"
              >
                <img
                  src={brand.icon}
                  alt={brand.name}
                  className="sk:h-8 sk:w-auto"
                />
              </Link>
              <GlobalSearch />
            </div>
            <OrganizationSwitcher />
          </div>
        }
        footer={
          <ProfileMenu
            pages={navigation.footer}
            accountPages={navigation.account}
            v2={isV2Account}
          />
        }
      />
      {isNavOpen ? (
        // Escape closes it too; the click is a convenience for a pointer.
        <div
          aria-hidden
          className="sk:fixed sk:inset-0 sk:z-40 sk:bg-black/30 sk:lg:hidden"
          onClick={() => setIsNavOpen(false)}
        />
      ) : null}
      <ExportReportModal
        sheet={showExportPopup ? exportSheetTypes.purchaseOrder : null}
        onClose={closeExportPopup}
      />
    </>
  );
}

export default Sidebar;
